// Share strings: a scene JSON, deflate-compressed and base64url-encoded behind
// a version prefix, short enough to paste into a chat message. Only the scene
// travels — print settings belong to the cart doing the printing, not to the
// piece being shared.

const PREFIX = "sb1:";

// Encode a scene JSON as a share string.
export async function encodeShare(sceneJson: string): Promise<string> {
  const deflated = await pipeBytes(
    new TextEncoder().encode(sceneJson),
    new CompressionStream("deflate-raw"),
  );
  return PREFIX + base64UrlEncode(deflated);
}

// Decode a share string back to its scene JSON, throwing if any stage of the
// pipeline rejects it or the result isn't JSON.
export async function decodeShare(text: string): Promise<string> {
  const trimmed = text.trim();
  if (!trimmed.startsWith(PREFIX)) {
    throw new Error(`not a share string (expected an "${PREFIX}" prefix)`);
  }

  let deflated: Uint8Array<ArrayBuffer>;
  try {
    deflated = base64UrlDecode(trimmed.slice(PREFIX.length));
  } catch {
    throw new Error("share string is not valid base64");
  }

  let inflated: Uint8Array;
  try {
    inflated = await pipeBytes(deflated, new DecompressionStream("deflate-raw"));
  } catch {
    throw new Error("share string is corrupt");
  }

  const json = new TextDecoder().decode(inflated);
  try {
    JSON.parse(json);
  } catch {
    throw new Error("share string does not hold a scene");
  }
  return json;
}

// Run bytes through a (de)compression stream and collect the result. The
// buffer types are spelled out because the streams only take unshared memory.
async function pipeBytes(
  bytes: Uint8Array<ArrayBuffer>,
  transform: CompressionStream | DecompressionStream,
): Promise<Uint8Array<ArrayBuffer>> {
  const source = new ReadableStream<BufferSource>({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });
  const piped = source.pipeThrough(transform);
  return new Uint8Array(await new Response(piped).arrayBuffer());
}

// base64url: base64 with the URL-hostile characters swapped and no padding, so
// the string survives being pasted anywhere.
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Throws (via atob) on anything that isn't base64.
function base64UrlDecode(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
